/**
 * HTTP klient telefonika — wyłącznie zamrożone endpointy §18 + schematy @pieskowo/shared.
 */
import {
  DogPutRequestSchema,
  DogPutResponseSchema,
  SettingsPutRequestSchema,
  SettingsPutResponseSchema,
  InvitesPendingResponseSchema,
  InviteRespondRequestSchema,
  InviteRespondResponseSchema,
  ResetRequestSchema,
  ResetResponseSchema,
  MeResponseSchema,
  type DogPutRequest,
  type DogPutResponse,
  type InvitesPendingResponse,
  type MeResponse,
} from '@pieskowo/shared';
import { apiBase, authHeaders } from '../core/apiBase.js';

async function parseJson(res: Response): Promise<unknown> {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

/** GET /api/me — profil + ustawienia (gdy token). */
export async function fetchMe(): Promise<MeResponse | null> {
  try {
    const res = await fetch(`${apiBase()}/api/me`, { headers: authHeaders() });
    if (!res.ok) return null;
    const json = await parseJson(res);
    const out = MeResponseSchema.safeParse(json);
    return out.success ? out.data : null;
  } catch {
    return null;
  }
}

/** PUT /api/profile/dog */
export async function putDog(
  body: DogPutRequest,
): Promise<DogPutResponse | null> {
  const parsed = DogPutRequestSchema.safeParse(body);
  if (!parsed.success) return null;
  try {
    const res = await fetch(`${apiBase()}/api/profile/dog`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(parsed.data),
    });
    if (!res.ok) return null;
    const json = await parseJson(res);
    const out = DogPutResponseSchema.safeParse(json);
    return out.success ? out.data : null;
  } catch {
    return null;
  }
}

/** PUT /api/profile/settings — musicOn / sfxOn */
export async function putSettings(
  musicOn: boolean,
  sfxOn: boolean,
): Promise<boolean> {
  const parsed = SettingsPutRequestSchema.safeParse({ musicOn, sfxOn });
  if (!parsed.success) return false;
  try {
    const res = await fetch(`${apiBase()}/api/profile/settings`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(parsed.data),
    });
    if (!res.ok) return false;
    const json = await parseJson(res);
    return SettingsPutResponseSchema.safeParse(json).success;
  } catch {
    return false;
  }
}

/** GET /api/invites/pending */
export async function fetchPendingInvites(): Promise<InvitesPendingResponse | null> {
  try {
    const res = await fetch(`${apiBase()}/api/invites/pending`, { headers: authHeaders() });
    if (!res.ok) return null;
    const json = await parseJson(res);
    const out = InvitesPendingResponseSchema.safeParse(json);
    return out.success ? out.data : null;
  } catch {
    return null;
  }
}

/** POST /api/invites/:id/respond */
export async function respondInvite(
  inviteId: string,
  accept: boolean,
): Promise<'accepted' | 'declined' | null> {
  const parsed = InviteRespondRequestSchema.safeParse({ accept });
  if (!parsed.success) return null;
  try {
    const res = await fetch(`${apiBase()}/api/invites/${encodeURIComponent(inviteId)}/respond`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(parsed.data),
    });
    if (!res.ok) return null;
    const json = await parseJson(res);
    const out = InviteRespondResponseSchema.safeParse(json);
    return out.success ? out.data.status : null;
  } catch {
    return null;
  }
}

/** POST /api/auth/verify-pin — bramka rodzica (PIN 4–8 cyfr). */
export async function verifyParentPin(parentCredential: string): Promise<boolean> {
  try {
    const res = await fetch(`${apiBase()}/api/auth/verify-pin`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ parentCredential }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * POST /api/auth/reset — po bramce rodzica (prawdziwy PIN, nie matma).
 * @returns 'ok' | 'unauthorized' | 'fail'
 */
export async function resetProfile(
  parentCredential: string,
): Promise<'ok' | 'unauthorized' | 'fail'> {
  const parsed = ResetRequestSchema.safeParse({
    confirm: true,
    parentCredential,
  });
  if (!parsed.success) return 'fail';
  try {
    const res = await fetch(`${apiBase()}/api/auth/reset`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(parsed.data),
    });
    if (res.status === 401) return 'unauthorized';
    if (!res.ok) return 'fail';
    const json = await parseJson(res);
    return ResetResponseSchema.safeParse(json).success ? 'ok' : 'fail';
  } catch {
    return 'fail';
  }
}
